import { useEffect, useRef } from 'react';
import type { TableFilterItem } from '../types';

interface UseAutoSelectBehaviorOptions {
  filterItems: TableFilterItem[];
  selectedValues: string[];
  setSelectedValues: (values: string[] | ((prev: string[]) => string[])) => void;
  initialSelectedValues?: string[];
  enabled?: boolean;
}

/**
 * Hook for auto-select behavior (stateless - manages no state itself)
 *
 * Features:
 * 1. Auto-select all items on first load (if no initial values and enabled)
 * 2. Auto-select newly loaded items during infinite scroll (if all previous items selected)
 *
 * This hook receives state from parent and just applies auto-select logic.
 * It does NOT manage selectedValues state - that's the parent's responsibility.
 *
 * @param filterItems - Available filter items
 * @param selectedValues - Current selected values (from parent state)
 * @param setSelectedValues - Setter function (from parent state)
 * @param initialSelectedValues - Initial values from parent (e.g., URL params)
 * @param enabled - Enable/disable auto-select behavior
 */
export const useAutoSelectBehavior = ({
  filterItems,
  selectedValues,
  setSelectedValues,
  initialSelectedValues = [],
  enabled = true,
}: UseAutoSelectBehaviorOptions) => {
  const hasInitializedRef = useRef(false);
  const prevLengthRef = useRef(0);
  const prevInitialValuesLengthRef = useRef(initialSelectedValues.length);

  // Detect filter reset: when initialSelectedValues changes from non-empty to empty
  // This means the user clicked "Clear All Filters" button
  useEffect(() => {
    const prevLength = prevInitialValuesLengthRef.current;
    const currentLength = initialSelectedValues.length;

    // Filter was reset (went from having values to being empty)
    if (prevLength > 0 && currentLength === 0 && filterItems.length > 0 && enabled) {
      // Auto-select all items (reset to default "Select All" state)
      setSelectedValues(filterItems.map((item) => item.id));
    }

    // Update ref for next comparison
    prevInitialValuesLengthRef.current = currentLength;
  }, [initialSelectedValues.length, filterItems, enabled, setSelectedValues]);

  // Auto-select all on first load (if enabled and no initial values)
  useEffect(() => {
    if (!enabled || hasInitializedRef.current || !filterItems.length) return;

    if (initialSelectedValues.length > 0) {
      // Use initial values from parent (e.g., URL params)
      setSelectedValues(initialSelectedValues);
    } else {
      // Auto-select all items by default on first load
      setSelectedValues(filterItems.map((item) => item.id));
    }
    hasInitializedRef.current = true;
  }, [filterItems.length, initialSelectedValues, enabled, setSelectedValues]);

  // Auto-select newly loaded items on infinite scroll (if all previous items are selected)
  useEffect(() => {
    if (!enabled || !hasInitializedRef.current) return;

    const currentLength = filterItems.length;
    const prevLength = prevLengthRef.current;

    // Check if new items were added
    if (currentLength > prevLength) {
      // Get IDs of items that existed before
      const previousItemIds = filterItems.slice(0, prevLength).map((item) => item.id);

      // Only auto-select if there were previous items AND all were selected
      // Empty array .every() returns true, which would incorrectly auto-select
      const allPreviousSelected = previousItemIds.length > 0 && previousItemIds.every((id) => selectedValues.includes(id));

      if (allPreviousSelected) {
        // Auto-select the new items too
        const newItemIds = filterItems.slice(prevLength).map((item) => item.id);
        setSelectedValues((prev) => [...prev, ...newItemIds]);
      }
    }

    // Update ref for next comparison
    prevLengthRef.current = currentLength;
  }, [filterItems, selectedValues, enabled, setSelectedValues]);

  // This hook has no return value - it's just side effects
};
